import { apiClient } from './client';
import type {
  UserRegisterRequest,
  UserRegisterResponse,
  UserLoginRequest,
  TokenResponse,
  UserResponse,
  VerifyEmailRequest,
  VerifyEmailResponse,
  ResendVerificationRequest,
  ResendVerificationResponse,
  RefreshTokenRequest,
  ResetPasswordRequest,
  ResetPasswordResponse,
  ChangePasswordRequest,
  ChangePasswordResponse,
  RequestEmailChangeRequest,
  VerifyEmailChangeRequest,
  EmailChangeResponse,
  UpdatePasswordRequest,
  UpdatePasswordResponse,
  UserTenantsResponse,
  TenantResponse,
  CreateTenantRequest,
  CreateTenantResponse,
  UpdateTenantRequest,
  UpdateTenantResponse,
  SwitchTenantRequest,
  SwitchTenantResponse,
  TenantDeletionInfoResponse,
  DeleteTenantRequest,
  DeleteTenantResponse,
  ChallengeResponse,
} from './types';

export const authApi = {
  getChallenge: async (): Promise<ChallengeResponse> => {
    const response = await apiClient.get('/auth/challenge');
    return response.data;
  },

  register: async (data: UserRegisterRequest): Promise<UserRegisterResponse> => {
    const response = await apiClient.post('/auth/register', data);
    return response.data;
  },

  login: async (data: UserLoginRequest): Promise<TokenResponse> => {
    const response = await apiClient.post('/auth/login', data);
    return response.data;
  },

  getCurrentUser: async (): Promise<UserResponse> => {
    const response = await apiClient.get('/auth/me');
    return response.data;
  },

  verifyEmail: async (data: VerifyEmailRequest): Promise<VerifyEmailResponse> => {
    const response = await apiClient.post('/auth/verify-email', data);
    return response.data;
  },

  resendVerification: async (data: ResendVerificationRequest): Promise<ResendVerificationResponse> => {
    const response = await apiClient.post('/auth/resend-verification', data);
    return response.data;
  },

  refreshToken: async (data: RefreshTokenRequest): Promise<TokenResponse> => {
    const response = await apiClient.post('/auth/refresh', data);
    return response.data;
  },

  resetPassword: async (data: ResetPasswordRequest): Promise<ResetPasswordResponse> => {
    const response = await apiClient.post('/auth/reset-password', data);
    return response.data;
  },

  changePassword: async (data: ChangePasswordRequest): Promise<ChangePasswordResponse> => {
    const response = await apiClient.post('/auth/change-password', data);
    return response.data;
  },

  requestEmailChange: async (data: RequestEmailChangeRequest): Promise<EmailChangeResponse> => {
    const response = await apiClient.post('/auth/request-email-change', data);
    return response.data;
  },

  verifyEmailChange: async (data: VerifyEmailChangeRequest): Promise<EmailChangeResponse> => {
    const response = await apiClient.post('/auth/verify-email-change', data);
    return response.data;
  },

  updatePassword: async (data: UpdatePasswordRequest): Promise<UpdatePasswordResponse> => {
    const response = await apiClient.post('/auth/update-password', data);
    return response.data;
  },

  /**
   * Get all tenants the user has access to
   */
  getTenants: async (): Promise<UserTenantsResponse> => {
    const response = await apiClient.get('/auth/tenants');
    return response.data;
  },

  /**
   * Get current tenant details
   */
  getTenant: async (): Promise<TenantResponse> => {
    const response = await apiClient.get('/auth/tenant');
    return response.data;
  },

  /**
   * Create a new tenant
   */
  createTenant: async (data: CreateTenantRequest): Promise<CreateTenantResponse> => {
    const response = await apiClient.post('/auth/tenants', data);
    return response.data;
  },

  /**
   * Update current tenant name
   */
  updateTenant: async (data: UpdateTenantRequest): Promise<UpdateTenantResponse> => {
    const response = await apiClient.patch('/auth/tenant', data);
    return response.data;
  },

  /**
   * Switch active tenant
   */
  switchTenant: async (data: SwitchTenantRequest): Promise<SwitchTenantResponse> => {
    const response = await apiClient.post('/auth/switch-tenant', data);
    return response.data;
  },

  /**
   * Get tenant deletion info (check if user can delete current tenant)
   */
  getTenantDeletionInfo: async (): Promise<TenantDeletionInfoResponse> => {
    const response = await apiClient.get('/auth/tenant/deletion-info');
    return response.data;
  },

  /**
   * Delete current tenant
   */
  deleteTenant: async (data: DeleteTenantRequest): Promise<DeleteTenantResponse> => {
    const response = await apiClient.delete('/auth/tenant', { data });
    return response.data;
  },

  logout: () => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
    }
  },
};